// Constants
import { FilterTypes } from "../constants/FilterConstants";
// Models
import { DayOfWeek, RelativeWeekIndex } from "@ms/uno-constants/lib/local/DateConstants";
import { ContextScenarioConstants } from "@ms/uno-constants/lib/local/ExternalSourceConstants";
import { Temporal } from "temporal-polyfill-lite";
import { ContentType } from "../client/planner/common/IItemBody";
import { CustomFieldValue } from "../client/planner/customFields/CustomFieldValue";
import { RecurrenceDropdownType } from "../client/planner/recurrence/RecurrenceDropdownType";
import { RecurrenceOccurrence } from "../client/planner/recurrence/RecurrenceOccurrence";
import { RecurrencePattern } from "../client/planner/recurrence/RecurrencePattern";
import { RecurrenceRange } from "../client/planner/recurrence/RecurrenceRange";
import { RecurrenceSchedule } from "../client/planner/recurrence/RecurrenceSchedule";
import { TaskPreviewType } from "../client/planner/task/ITask";
import { AssignMode, BulkUpdatableField } from "../client/planner/task/Enumerations";
import { customFieldValueToStorageString } from "../client/planner/customFields/coerceCustomFieldStringValue";
import { Task } from "../client/planner/task/Task";
import { TaskPersonalViewOrderHints } from "../client/planner/task/TaskPersonalViewOrderHints";
import { TaskPlanViewOrderHints } from "../client/planner/task/TaskPlanViewOrderHints";
import { FilterFields } from "../constants/FilterConstants";
import { RecurrenceEndType, RecurrencePatternType, TaskStatus } from "../service/planner/enumerations/IPlannerEnumerations";
// Utilities
import { compareOrderHints } from "@ms/uno-utilities/lib/local/Comparators";
import { getDayOfWeekFromIndex, getWeekIndex } from "@ms/uno-utilities/lib/local/DateUtilities-temporal";
import { LocalIdGenerator } from "@ms/uno-utilities/lib/local/LocalIdGenerator";
import { generateOrderHintBetween } from "@ms/uno-utilities/lib/local/OrdinalUtilities";
/**
 * Returns the calendar event iCalUId encoded in meeting-created task metadata.
 * @param task Task whose creation metadata may identify a Teams meeting
 */ export function getICalUIdFromTask(task) {
    const scenarioId = task.creationInfo?.scenarioId;
    const isMeetingCreatedTask = scenarioId === ContextScenarioConstants.meetingPlan || scenarioId === ContextScenarioConstants.meetingPlanScheduled;
    return isMeetingCreatedTask ? task.creationInfo?.scope?.split("~")[0] || null : null;
}
/**
 * Returns the distinct calendar event iCalUIds across the given meeting-created tasks.
 * @param tasks Tasks to scan for meeting metadata
 */ export function getDistinctMeetingICalUIds(tasks) {
    const iCalUIds = new Set();
    for (const task of tasks){
        const iCalUId = getICalUIdFromTask(task);
        if (iCalUId) {
            iCalUIds.add(iCalUId);
        }
    }
    return Array.from(iCalUIds);
}
/**
 * Returns a map of categories that are in use by at least one task or has been renamed by the user
 * @param tasks Task data
 * @param categories Categories from the Plan data. If value is a string, it is a custom label name. If null, it is the default label name.
 * @returns Map where the key is the category index, and value of true means the category is in use
 */ export function getUsedCategoriesMap(tasks, categories) {
    // Determine subset of labels that are used by at least one task
    const usedCategories = tasks.reduce((_usedCategories, currentTask)=>{
        // In the new planner model, applied category IDs are in planView.appliedCategoryIds
        currentTask.planView.appliedCategoryIds.forEach((categoryId)=>{
            // Convert categoryId to numeric index if it's a number string
            const categoryIndex = parseInt(categoryId, 10);
            // If categoryIndex is a valid number, mark it as used
            if (!isNaN(categoryIndex)) {
                _usedCategories[categoryIndex] = true;
            }
        });
        return _usedCategories;
    }, {});
    // Also include categories that have been renamed by the user
    categories.forEach((categoryName, category)=>{
        if (categoryName !== null && categoryName.length > 0) {
            usedCategories[category] = true;
        }
    });
    return usedCategories;
}
/**
 * Returns a map of user IDs that are assigned to at least one task
 * @param tasks Task data
 * @returns Map where the key is the user ID, and value of true means user has at least one assignment
 */ export function getTaskAssigneeIdsMap(tasks) {
    const userIdsWithAssignments = tasks.reduce((_userIdsWithAssignments, currentTask)=>{
        // In the new planner model, user assignments are in the userAssignments array
        currentTask.userAssignments.forEach((assignment)=>{
            _userIdsWithAssignments[assignment.user.id] = true;
        });
        return _userIdsWithAssignments;
    }, {});
    return userIdsWithAssignments;
}
/**
 * Returns all the referenced user ids for the task
 * @param task Task for which the referenced users are needed
 * @returns an array of referenced user ids
 */ export function getTaskReferencedUserIds(task) {
    const assignments = task.userAssignments.map((userAssignment)=>userAssignment.user.id);
    const createdByUser = task.creationInfo.createdBy.user;
    // if createdBy user is not already a part of assignments and is not empty
    if (createdByUser.id && assignments.indexOf(createdByUser.id) === -1) {
        assignments.push(task.creationInfo.createdBy.user.id);
    }
    return assignments;
}
/**
 * Filters the tasks based on unique equivalence set ids for all project tasks
 * and returns 1 project task per equivalence set id
 * @param tasks tasks to be filtered
 */ export function filterUniqueTasksBasedOnEquivalenceSetIds(tasks) {
    const equivalenceSetIdToTaskMapping = {};
    const filteredTasks = [];
    for (const task of tasks){
        // planId is a placeholder, should be replaced with new field for equivalence set id on task
        const equivalenceSetId = task.planId;
        // In new planner model, project task information might be stored differently
        // Using conversationThreadId as a placeholder to determine if it's a project task
        const isProjectTask = Boolean(task.extensions.conversationThreadId);
        // If it's a project task and not already mapped, add it to the filtered tasks
        if (isProjectTask && !equivalenceSetIdToTaskMapping[equivalenceSetId]) {
            equivalenceSetIdToTaskMapping[equivalenceSetId] = task;
            filteredTasks.push(task);
        }
    }
    return filteredTasks;
}
/**
 * Determines whether or not an update contains a checklist item reorder
 * @param updatedChecklistItems Record of updated checklist items
 * @param originalChecklistItems The original checklist items array (unified model)
 */ export function updateContainsChecklistItemReorder(updatedChecklistItems, originalChecklistItems) {
    // Check for reorder by comparing orderHint values in extensions.LegacyData
    // In unified model, orderHint is stored in extensions.LegacyData.orderHint
    return Object.values(updatedChecklistItems).some((checklistItem)=>{
        const originalChecklistItem = originalChecklistItems.find((item)=>item.id === checklistItem.id);
        // If the original checklist item is not found, we can't determine reorder
        if (!originalChecklistItem) {
            return false;
        }
        // Extract orderHint from unified model extensions.LegacyData.orderHint
        const getOrderHint = (item)=>{
            return item.extensions.orderHint;
        };
        const originalOrderHint = getOrderHint(originalChecklistItem);
        const updatedOrderHint = getOrderHint(checklistItem);
        // If both have orderHint values and they're different, it's a reorder
        return Boolean(originalOrderHint && updatedOrderHint && originalOrderHint !== updatedOrderHint);
    });
}
/**
 * Create a temporary task
 * @param taskId Id of the temp task to create
 * @param planId Id of the plan
 * @param taskKind The kind of task to create
 * @param goalId Optional goal ID to associate with the task
 * @param name Optional name of the task
 */ export function createTempTask(taskId, planId, taskKind, goalId, name, overrides) {
    // Create a new task with required properties for the new planner model
    const now = Temporal.Now.instant();
    const tempTask = new Task({
        id: taskId,
        version: "1",
        archivalInfo: {
            isArchived: false,
            justification: null,
            modifiedBy: null,
            modifiedDate: null
        },
        capabilities: {
            entityExtensions: false
        },
        userAssignments: [],
        bucketId: null,
        checklist: [],
        creationInfo: {
            createdBy: {
                user: {
                    id: "",
                    tenantId: ""
                },
                application: {
                    id: "",
                    tenantId: ""
                }
            },
            createdDateTime: now,
            externalId: null,
            externalVersion: null,
            scenarioId: null,
            scope: null,
            externalUrl: null
        },
        dependencies: [],
        displayName: name ?? "",
        dueDateTime: overrides?.dueDateTime ?? null,
        extensions: {
            conversationThreadId: null
        },
        finishDateTime: null,
        goalIds: goalId ? [
            goalId
        ] : [],
        hasChildren: false,
        isStarred: false,
        kind: taskKind,
        lastModifiedBy: {
            user: {
                id: "",
                tenantId: ""
            },
            application: {
                id: "",
                tenantId: ""
            }
        },
        lastModifiedDateTime: now,
        links: [],
        notes: {
            content: "",
            contentType: ContentType.HTML
        },
        parentTaskId: null,
        permissionSetId: "",
        planId: planId,
        priority: overrides?.priority ?? 5,
        personalView: {
            isOnMyDay: false,
            orderHints: new TaskPersonalViewOrderHints()
        },
        planView: {
            appliedCategoryIds: [],
            previewType: TaskPreviewType.NoPreview,
            orderHints: new TaskPlanViewOrderHints()
        },
        startDateTime: null,
        percentComplete: overrides?.percentComplete ?? 0,
        status: overrides?.status ?? TaskStatus.NotStarted,
        completedBy: null,
        completedDateTime: null
    });
    return tempTask;
}
/**
 * Returns a list of users that have task assignments, or if the filter option for that user is applied
 * @param users Users data
 * @param tasks Tasks data
 * @param taskFilter Current task filter
 */ export function getUsersForAssignmentFilterOptions(users, tasks, taskFilter) {
    // Determine set of user IDs who have task assignments
    const userIdsWithAssignments = getTaskAssigneeIdsMap(tasks);
    // Only render user assignment filter options for users that have task assignments,
    // or if the filter option is already applied.
    const usersToRender = users.filter((user)=>userIdsWithAssignments[user.id] || taskFilter.doesFilterValueExist(FilterFields[FilterTypes.UserAssignments], user.id));
    return usersToRender;
}
/**
 * Returns a list of categories that are in use, or if the filter option for that category is applied
 * @param categories Categories from the Plan data. If value is a string, it is a custom label name. If null, it is the default label name.
 * @param tasks Tasks data
 * @param taskFilter Current task filter
 */ export function getCategoryFilterOptions(categories, tasks, taskFilter) {
    let categoryFilterOptions = categories.map((categoryName, categoryNumber)=>({
            categoryNumber,
            categoryName
        }));
    if (tasks != null) {
        // Determine labels/categories that are used by at least one task
        const usedCategories = getUsedCategoriesMap(tasks, categories);
        // Only render category filter options that are used by at least one task,
        // or if the category filter is already applied.
        categoryFilterOptions = categoryFilterOptions.filter(({ categoryNumber })=>usedCategories[categoryNumber] || taskFilter.doesFilterValueExist(FilterFields[FilterTypes.AppliedCategoryIds], categoryNumber.toString()));
    }
    return categoryFilterOptions;
}
/**
 * Create a temporary recurrence occurrence for a task using the schedule or defaulting to a temporary one
 * @param task Task to create the recurrence occurrence for
 * @param schedule Optional recurrence schedule to use for the occurrence
 * @see createRecurrenceSchedule
 */ export function createTempRecurrenceOccurrence(task, schedule) {
    return new RecurrenceOccurrence({
        seriesId: "temp-series-id-for-" + task.id,
        occurrenceId: 1,
        previousInSeriesTaskId: null,
        nextInSeriesTaskId: null,
        recurrenceStartDateTime: Temporal.Now.instant(),
        // Default to a temp recurrence schedule if none provided
        schedule: schedule ?? createRecurrenceSchedule()
    });
}
/**
 * Create a recurrence schedule using optional pattern with default range of EndOfPlan type starting today
 * @param pattern Optional recurrence pattern for the recurrence schedule, otherwise defaults to daily pattern
 */ export function createRecurrenceSchedule(pattern) {
    return new RecurrenceSchedule({
        pattern: pattern ?? // Default to daily pattern if none provided
        RecurrencePattern.fromResource({
            dayOfMonth: 0,
            daysOfWeek: [],
            firstDayOfWeek: DayOfWeek.Sunday,
            index: RelativeWeekIndex.First,
            interval: 1,
            month: 0,
            type: RecurrencePatternType.Daily
        }),
        range: RecurrenceRange.fromResource({
            startDate: Temporal.Now.instant().toString(),
            recurrenceEndType: RecurrenceEndType.EndOfPlan,
            endDate: null,
            countOfInstances: null
        }),
        nextOccurrenceDateTime: null
    });
}
const WEEKDAY_PATTERN_DAYS = [
    DayOfWeek.Monday,
    DayOfWeek.Tuesday,
    DayOfWeek.Wednesday,
    DayOfWeek.Thursday,
    DayOfWeek.Friday
];
const DEFAULT_FIRST_DAY_OF_WEEK = DayOfWeek.Sunday; // TODO Task 2786922: Get locale specific first day of week
function resolveDaysOfWeek(options) {
    if (options?.daysOfWeek && options.daysOfWeek.length > 0) {
        return options.daysOfWeek;
    }
    if (options?.patternDate) {
        return [
            getDayOfWeekFromIndex(options.patternDate.temporal)
        ];
    }
    return [
        DayOfWeek.Monday
    ];
}
function resolveWeekIndex(options) {
    if (options?.weekIndex != null) {
        return options.weekIndex;
    }
    if (options?.patternDate) {
        return getWeekIndex(options.patternDate.date);
    }
    return RelativeWeekIndex.First;
}
/**
 * Create a recurrence pattern from a dropdown type with pattern date used for certain patterns
 * @param recurrenceDropdownType Dropdown type to create the pattern from
 * @param options Optional metadata to drive the resulting recurrence pattern
 */ export function createRecurrencePatternFromDropdownType(recurrenceDropdownType, options) {
    const interval = options?.interval && !isNaN(options.interval) && options.interval > 0 ? options.interval : 1;
    const daysOfWeek = resolveDaysOfWeek(options);
    const dayOfMonth = options?.dayOfMonth ?? options?.patternDate?.date.day ?? 1;
    const month = options?.month ?? options?.patternDate?.date.month ?? 1;
    const weekIndex = resolveWeekIndex(options);
    const isAbsolute = options?.isAbsolute ?? true;
    switch(recurrenceDropdownType){
        case RecurrenceDropdownType.Weekdays:
            return new RecurrencePattern({
                patternType: RecurrencePatternType.Weekly,
                interval: 1,
                daysOfWeek: WEEKDAY_PATTERN_DAYS,
                firstDayOfWeek: DEFAULT_FIRST_DAY_OF_WEEK,
                dayOfMonth: null,
                dayOfWeek: null,
                month: null,
                weekIndex: null
            });
        case RecurrenceDropdownType.Weekly:
            if (interval === 1) {
                // All 7 days selected is equivalent to daily recurrence
                if (daysOfWeek.length === 7) {
                    return new RecurrencePattern({
                        patternType: RecurrencePatternType.Daily,
                        interval: 1,
                        dayOfMonth: null,
                        dayOfWeek: null,
                        daysOfWeek: null,
                        firstDayOfWeek: null,
                        month: null,
                        weekIndex: null
                    });
                }
                return new RecurrencePattern({
                    patternType: RecurrencePatternType.Weekly,
                    interval: 1,
                    dayOfWeek: null,
                    daysOfWeek: daysOfWeek,
                    firstDayOfWeek: DEFAULT_FIRST_DAY_OF_WEEK,
                    dayOfMonth: null,
                    month: null,
                    weekIndex: null
                });
            }
            return new RecurrencePattern({
                patternType: RecurrencePatternType.Weekly,
                interval,
                dayOfWeek: daysOfWeek[0],
                daysOfWeek: null,
                firstDayOfWeek: DEFAULT_FIRST_DAY_OF_WEEK,
                dayOfMonth: null,
                month: null,
                weekIndex: null
            });
        case RecurrenceDropdownType.Monthly:
            if (isAbsolute) {
                return new RecurrencePattern({
                    patternType: RecurrencePatternType.FixedDayOfMonth,
                    interval,
                    dayOfMonth,
                    dayOfWeek: null,
                    daysOfWeek: null,
                    firstDayOfWeek: null,
                    month: null,
                    weekIndex: null
                });
            }
            return new RecurrencePattern({
                patternType: RecurrencePatternType.RelativeDayOfMonth,
                interval,
                dayOfMonth: null,
                dayOfWeek: daysOfWeek[0],
                daysOfWeek: null,
                firstDayOfWeek: null,
                month: null,
                weekIndex
            });
        case RecurrenceDropdownType.Yearly:
            if (isAbsolute) {
                return new RecurrencePattern({
                    patternType: RecurrencePatternType.FixedDayOfYear,
                    interval,
                    dayOfMonth,
                    month,
                    dayOfWeek: null,
                    daysOfWeek: null,
                    firstDayOfWeek: null,
                    weekIndex: null
                });
            }
            return new RecurrencePattern({
                patternType: RecurrencePatternType.RelativeDayOfYear,
                interval,
                dayOfMonth: null,
                dayOfWeek: daysOfWeek[0],
                daysOfWeek: null,
                firstDayOfWeek: null,
                month,
                weekIndex
            });
        case RecurrenceDropdownType.Daily:
            return new RecurrencePattern({
                patternType: RecurrencePatternType.Daily,
                interval,
                dayOfMonth: null,
                dayOfWeek: null,
                daysOfWeek: null,
                firstDayOfWeek: null,
                month: null,
                weekIndex: null
            });
        case RecurrenceDropdownType.NotRepeat:
        case RecurrenceDropdownType.Custom:
        default:
            return null;
    }
}
/**
 * Get checklist items sorted by order hint in reversed order
 * @param checklistItems Array of checklist items to sort
 * @returns Sorted array of checklist items
 */ export function getSortedChecklistItems(checklistItems) {
    return checklistItems.slice().sort((item1, item2)=>{
        return -1 * compareOrderHints(item1.getOrderHint(), item2.getOrderHint());
    });
}
/**
 * Create a copy of the checklist with correct order hints
 * @param checklist Checklist items to create a copy of
 * @returns New checklist items array with correct order hints
 */ export function createChecklistCopyWithCorrectOrderHint(checklist) {
    const asciiValueOfZ = "Z".charCodeAt(0);
    if (checklist) {
        // There is a max of 20 checklist items and reversed ordered after getSortedChecklistItems, if we start the ascii value of the 'Z', we can go up to 'G'
        const sortedItems = getSortedChecklistItems(checklist);
        const checklistArray = sortedItems.map((item, index)=>{
            const tempOrderHint = String.fromCharCode(asciiValueOfZ - index);
            return item.setOrderHint(tempOrderHint);
        });
        const finalArray = checklistArray.map((item, index)=>{
            let newOrderHint;
            if (index === 0) {
                newOrderHint = generateOrderHintBetween(item.getOrderHint(), null);
            } else {
                newOrderHint = generateOrderHintBetween(item.getOrderHint(), checklistArray[index - 1].getOrderHint());
            }
            return item.setOrderHint(newOrderHint);
        });
        return finalArray;
    }
}
/**
 * Sort by preview priority in reversed order
 * @param contentLinks Array of content links to sort
 * @param links sorted array of content links
 */ function getSortedLinks(contentLinks) {
    return contentLinks.slice().sort((item1, item2)=>{
        return -1 * compareOrderHints(item1.extensions.previewPriority, item2.extensions.previewPriority);
    });
}
/**
 * Create a copy of the custom field values with new entity IDs
 * @param customFieldValues Custom field values to create a copy of
 * @returns New custom field values array with fresh IDs preserving field bindings and values
 */ export function createCustomFieldValuesCopy(customFieldValues) {
    return customFieldValues.map((fieldValue)=>new CustomFieldValue({
            id: LocalIdGenerator.generate(),
            version: "1",
            customFieldDefinitionId: fieldValue.customFieldDefinitionId,
            value: fieldValue.value
        }));
}
/**
 * Resolves the optimistic reactivation target status for a task based on the EnablePercentComplete flight.
 * Centralizes the flight -> status mapping so callers don't duplicate the ternary and can't accidentally
 * default to NotStarted when the flight is on.
 * ToDo: Remove this function and use InProgress once the flight is fully rolled out and the NotStarted status is no longer used for reactivation.
 * @param enablePercentComplete Whether the EnablePercentComplete flight is on
 */ export function getReactivateStatus(enablePercentComplete) {
    return enablePercentComplete ? TaskStatus.InProgress : TaskStatus.NotStarted;
}
/**
 * Toggles a task's completion state, using the flight-aware reactivate target for the reactivation direction.
 * Use for the "generic complete/reactivate" gesture (complete button, uncheck/check a task complete).
 * For status transitions where the caller already knows the target status (e.g., cross-group drops on a
 * Status-grouped view), use `changeTaskStatus` instead.
 * @param task The task to toggle
 * @param enablePercentComplete Whether the EnablePercentComplete flight is on
 */ export function toggleTaskCompletion(task, enablePercentComplete) {
    return task.isComplete() ? task.reactivateTask(getReactivateStatus(enablePercentComplete)) : task.completeTask();
}
/**
 * Compute the updated task for a status transition (e.g., cross-group drop on a Status-grouped board,
 * or setting status from the details editor).
 *
 * - Target `Completed` → `completeTask()` (also brings percentComplete to 100).
 * - Target `InProgress` / `NotStarted` when the task is currently complete → `reactivateTask` so
 *   percentComplete is reset to a value consistent with the new status.
 * - Target `InProgress` / `NotStarted` when the task is not currently complete → `setProperty("status", …)`.
 * @param task The source task.
 * @param targetStatus The status the task should transition to.
 * @param enablePercentComplete Whether the EnablePercentComplete flight is on.
 */ export function changeTaskStatus(task, targetStatus, enablePercentComplete) {
    if (targetStatus === TaskStatus.Completed) {
        return task.completeTask();
    }
    // For premium (project) tasks, keep percentComplete in sync with the target status based on the legacy
    // flight-independent mapping of NotStarted->0 and InProgress->50.
    const isPremiumTask = task.creationInfo?.scenarioId === ContextScenarioConstants.project;
    if (isPremiumTask) {
        const targetPercentComplete = targetStatus === TaskStatus.NotStarted ? 0 : 50;
        if (task.isComplete()) {
            return task.reactivateTask(targetStatus, targetPercentComplete);
        }
        return task.setProperty("status", targetStatus).setProperty("percentComplete", targetPercentComplete);
    }
    if (task.isComplete()) {
        //if enablePercentComplete is false, we want to reactivate the task with InProgress,50 otherwise reactivate with InProgress,0
        if (targetStatus === TaskStatus.InProgress && !enablePercentComplete) {
            return task.reactivateTask(TaskStatus.InProgress, 50);
        }
        return task.reactivateTask(targetStatus);
    }
    return task.setProperty("status", targetStatus);
}
/**
 * Compute the updated task for a percent-complete transition (e.g., changing percent complete from
 * the details editor spinner).
 *
 * - Target 100 → `completeTask()` (also brings status to Completed).
 * - Target < 100 when the task is currently complete → `reactivateTask` with the flight-aware status
 *   so the task exits the completed state, applying the new percent complete value.
 * - Target < 100 when the task is not currently complete → `setProperty("percentComplete", …)`.
 * @param task The source task.
 * @param targetPercentComplete The percent complete the task should transition to.
 * @param enablePercentComplete Whether the EnablePercentComplete flight is on.
 */ export function changePercentComplete(task, targetPercentComplete, enablePercentComplete) {
    if (targetPercentComplete === 100) {
        return task.completeTask();
    }
    if (task.isComplete()) {
        return task.reactivateTask(getReactivateStatus(enablePercentComplete), targetPercentComplete);
    }
    return task.setProperty("percentComplete", targetPercentComplete);
}
/**
 * Create a copy of the content links with correct priority order
 * @param contentLinks Content links to create a copy of
 * @returns New content links array with correct order hints
 */ export function createContentLinkCopyWithCorrectPriorityOrder(contentLinks) {
    const asciiValueOfZ = "Z".charCodeAt(0);
    if (contentLinks) {
        // There is a max of 10 reference items and reversed ordered after getSortedLinks, if we start the ascii value of the 'Z', we can go up to 'Q'
        const sortedItems = getSortedLinks(contentLinks);
        const linksList = sortedItems.map((item, index)=>{
            const tempPreviewPriority = String.fromCharCode(asciiValueOfZ - index);
            return item.setProperty("extensions", {
                ...item.extensions,
                previewPriority: tempPreviewPriority
            });
        });
        const finalArray = linksList.map((item, index)=>{
            let newPreviewPriority;
            if (index === 0) {
                newPreviewPriority = generateOrderHintBetween(linksList[index].extensions.previewPriority, null);
            } else {
                newPreviewPriority = generateOrderHintBetween(linksList[index].extensions.previewPriority, linksList[index - 1].extensions.previewPriority);
            }
            return item.setProperty("extensions", {
                ...item.extensions,
                previewPriority: newPreviewPriority
            });
        });
        return finalArray;
    }
}
/**
 * Whether a task now holds the value a bulk update asked for. Callers may be generic over the field,
 * so object values are narrowed structurally rather than by the field.
 * @example taskHoldsBulkUpdate(task, BulkUpdatableField.Priority, 5)
 * @param task The task, or null when it is not in the store
 * @param field The field that was set
 * @param value The value it was set to
 */ export function taskHoldsBulkUpdate(task, field, value) {
    if (task == null) {
        return false;
    }
    switch(field){
        // These field names are also the task property names, so the requested value is compared
        // against the property the field names.
        case BulkUpdatableField.Priority:
        case BulkUpdatableField.Status:
        case BulkUpdatableField.PercentComplete:
            return task[field] === value;
        // Compared as instants because the store rebuilds the value; the reference check skips that
        // conversion when it did not.
        case BulkUpdatableField.DueDateTime:
        case BulkUpdatableField.StartDateTime:
            {
                const current = task[field];
                if (current === value) {
                    return true;
                }
                return current != null && typeof value === "object" && "toUtcInstantString" in value && current.toUtcInstantString() === value.toUtcInstantString();
            }
        case BulkUpdatableField.Assignments:
            // Assigning adds the user and unassigning removes them, so the check is the presence of
            // that one user rather than the whole assignment set.
            return typeof value === "object" && "assignMode" in value && task.getAssigneeIds().includes(value.userId) === (value.assignMode === AssignMode.Assign);
        case BulkUpdatableField.CustomField:
            {
                if (typeof value !== "object" || !("customFieldDefinitionId" in value)) {
                    return false;
                }
                // Values are stored string-encoded by type, so encode before comparing. Clearing leaves
                // either no value for the definition or an empty one.
                const current = task.customFieldValues.find((existing)=>existing.customFieldDefinitionId === value.customFieldDefinitionId);
                return value.value == null ? current == null || current.value === "" : current?.value === customFieldValueToStorageString(value.value);
            }
        default:
            // Only reached by a field added without a check here, so report it as not held rather
            // than claiming work was verified.
            return false;
    }
}
